import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Box,
  Menu,
  MenuItem,
  CssBaseline,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Church as ChurchIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Send as SendIcon,
  Receipt as ReceiptIcon,
  AssignmentReturn as AssignmentReturnIcon,
  MenuBook as MenuBookIcon,
  Apps as AppsIcon,
  MonitorHeart as MonitorHeartIcon,
  Language as LanguageIcon,
  Gavel as GavelIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';

import { logout, selectUser } from '../../auth/authSlice';

interface SuperAdminMainLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 240;

export default function SuperAdminMainLayout({ children }: SuperAdminMainLayoutProps) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    dispatch(logout());
    window.location.href = user?.userType === 'contador' ? '/contador' : '/login';
  };

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon />, roles: ['super_admin'] },
    { to: '/parishes', label: 'Paróquias', icon: <ChurchIcon />, roles: ['super_admin'] },
    { to: '/crm', label: 'CRM produtores', icon: <GroupsIcon />, roles: ['super_admin'] },
    { to: '/contratos', label: 'Contratos', icon: <GavelIcon />, roles: ['super_admin'] },
    { to: '/custom-pages', label: 'Páginas personalizadas', icon: <LanguageIcon />, roles: ['super_admin'] },
    { to: '/events', label: 'Eventos', icon: <EventIcon />, roles: ['super_admin'] },
    { to: '/bulk-messages', label: 'Mensagens em Massa', icon: <SendIcon />, roles: ['super_admin'] },
    { to: '/transactions', label: 'Transações', icon: <PaymentIcon />, roles: ['super_admin'] },
    { to: '/payment-queues', label: 'Filas de pagamento', icon: <MonitorHeartIcon />, roles: ['super_admin'] },
    { to: '/refunds', label: 'Reembolso', icon: <AssignmentReturnIcon />, roles: ['super_admin'] },
    { to: '/invoice-issuance', label: 'Emissão de Nota', icon: <ReceiptIcon />, roles: ['super_admin', 'contador'] },
    { to: '/contadores', label: 'Acesso Contador', icon: <AccountCircle />, roles: ['super_admin'] },
    { to: '/app-para-app', label: 'App para App', icon: <AppsIcon />, roles: ['super_admin'] },
    { to: '/reports', label: 'Relatórios', icon: <BarChartIcon />, roles: ['super_admin'] },
    { to: '/documentation', label: 'Documentação', icon: <MenuBookIcon />, roles: ['super_admin'] },
    { to: '/settings', label: 'Configurações', icon: <SettingsIcon />, roles: ['super_admin'] },
  ].filter((item) => item.roles.includes(user?.userType || 'super_admin'));

  const drawerTitle = user?.userType === 'contador' ? 'Portal Contador' : 'Admin Paróquias';

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap>
          {drawerTitle}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {navItems.map(({ to, label, icon }) => (
          <ListItem
            button
            key={to}
            component={Link}
            to={to}
            selected={location.pathname === to || (to !== '/admin-master' && location.pathname.startsWith(to))}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={label} />
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItem button onClick={handleLogout}>
          <ListItemIcon><LogoutIcon /></ListItemIcon>
          <ListItemText primary="Sair" />
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', maxWidth: '100%', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ width: { lg: `calc(100% - ${drawerWidth}px)` }, ml: { lg: `${drawerWidth}px` } }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="inherit">
            <NotificationsIcon />
          </IconButton>
          <IconButton onClick={handleMenu} color="inherit">
            <Avatar>{user?.name?.charAt(0) || 'A'}</Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.name || 'Admin'}
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleClose}>
              <AccountCircle fontSize="small" sx={{ mr: 1 }} />
              Perfil
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Sair
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth, overflowY: 'auto' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth, overflowY: 'auto' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: 1,
          maxWidth: '100%',
          boxSizing: 'border-box',
          p: { xs: 1.5, sm: 2, md: 3 },
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
